/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { PullColumn } from '@touchplan/common';
import { SpritePool, TextPool } from '../../../../app-data/models/data-types/GenericPool';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { cssVar } from '@touchplan/css-var';
import {
	indicatorArrowFactory,
	timelineActiveTextPoolFactory,
} from '@appPlanApplication/timeline/TimelineHelpers';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { takeUntil } from 'rxjs/operators';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { IActiveColumn } from '../../../../modules/plan/services/active-column.service';
import { ValuePool } from '../../../../app-data/models/data-types/ValuePool';

const settings = {
	dateColor: cssVar.white,
	minDateColor: cssVar.grey60,
	textSize: 12,
	timelineHeight: 38,
	indicatorDay: `Sun`,
	columnWidth: 145,
};

export class ActiveSpaceTimelineDrawer extends AbstractAreaDrawer {
	private _cachedVisibleColumns: IActiveColumn[] | null;
	/**
	 * We store the last Active Line as a cache to handle a special case from old visibility function that is intended
	 * to prevent the indicator arrows from drawing above the active line when scaled out.
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 * ValuePool contains an object pool of HighResolutionText object. The ValuePool will automatically recycle text objects
	 * as needed to ensure we are not always creating or destroying multiple objects as the pull space timeline is re-drawn
	 * @private
	 */
	private _textPool: ValuePool<HighResolutionText> = ValuePool.createTextValuePool(
		timelineActiveTextPoolFactory,
		this._container
	);
	/**
	 * SpritePool contains an object pool of FancySprite objects. It automatically recycles objects as needed to ensure
	 * we are not always creating or destroying multiple objects as the active space timeline is re-drawn
	 * @private
	 */
	private _iconPool: SpritePool = new SpritePool(indicatorArrowFactory, this._container);
	/**
	 * Create a new instance of the ActiveSpaceTimelineDrawer. This Drawer is intended to be controlled by the ActiveSpaceDrawer
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.UI_TIMELINE_ACTIVE_SPACE);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}
	/**
	 * Draw the Pull Space Timeline Visual. This includes:
	 * - Date text on the timeline
	 * - Orange triangle sprite indicator
	 * @param visibleColumns PullColumn[] The pull columns to draw (after spread/nutella algorithm applied)
	 */
	public draw(visibleColumns: IActiveColumn[] | null = this._cachedVisibleColumns): void {
		if (!visibleColumns || !visibleColumns.length) {
			this.hide();
			return;
		}
		this._cachedVisibleColumns = visibleColumns;

		this.update();

		// We need to reset the text pool and sprite pool to reset internal pointers and refresh the screen with a new draw
		this._textPool.reset();
		this._iconPool.reset();

		// Parent draw clears our graphics drawing data and sets the container to visible
		super.draw();

		visibleColumns.forEach((column) => {
			this.createTimelineText(column);
			if (column.minDate === settings.indicatorDay) {
				this.createIndicator(column);
			}
			if (this._canvasAngularLand.activeColumnService.shouldDrawInner()) {
				this.createDayText(column);
			}
		});
	}

	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public update(_?: number): void {
		if (!this._cachedVisibleColumns) {
			return;
		}

		const data: IActiveColumn[] = this._cachedVisibleColumns;
		// It's possible that the visibleColumns is an empty array, so we terminate silently if so.
		// (We cannot position if there are no columns in the view)
		if (!data.length) {
			return;
		}
		const yOffset: number = settings.timelineHeight / 2;
		super.update(
			this.parentApplication.camera.realToScreenX(this._cachedActiveLine.view.activeLineX),
			cssVar.headerHeight_px + yOffset
		);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.sourceOnly): void {
		super.destroy(options);
		this._iconPool.destroy(DestroyOptions.sourceOnly);
		this._textPool.destroy(DestroyOptions.sourceOnly);
		this._textPool = null;
		this._iconPool = null;
		this._canvasAngularLand = null;
		this._cachedVisibleColumns = null;
		this._cachedActiveLine = null;
	}

	/**
	 * Create the timeline date text label.
	 * This is invoked as part of the draw() loop over the pull columns
	 * @param column
	 * @private
	 */
	private createTimelineText(column: IActiveColumn): void {
		const text: string = column.fullDate !== undefined ? column.fullDate : column.dayDate;
		const textObject: HighResolutionText = this._textPool.grab(text) as HighResolutionText;
		textObject.style.fill = settings.dateColor;
		textObject.style.fontSize = settings.textSize;
		// Using 0 for the Y because we want the text to always draw at the same Y value relative to the container
		textObject.position.set(
			this.parentApplication.camera.realToScreenX(column.finishX - settings.columnWidth / 2) -
				this._container.x,
			0
		);
	}

	private createDayText(column: IActiveColumn): void {
		const textObject: HighResolutionText = this._textPool.grab(column.minDate) as HighResolutionText;
		textObject.style.fill = settings.minDateColor;
		textObject.style.fontSize = settings.textSize;
		// Using 0 for the Y because we want the text to always draw at the same Y value relative to the container
		textObject.position.set(
			this.parentApplication.camera.realToScreenX(column.finishX - settings.columnWidth / 2) -
				this._container.x,
			24
		);
	}

	/**
	 * Create the orange triangle as a visual indicator of the pull columns
	 * @param column
	 * @private
	 */
	private createIndicator(column: PullColumn): void {
		const spriteObject: FancySprite = this._iconPool.grab() as FancySprite;
		spriteObject.position.set(
			this.parentApplication.camera.realToScreenX(column.finishX) - this._container.x,
			settings.timelineHeight / 2 + spriteObject.height / 2
		);
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((line) => (this._cachedActiveLine = line));
	}
}
